import { type ReactNode, useState } from 'react';
import { Card } from '../ui/card';
import { Modal } from '../ui/modal';
import { InfoButton } from '../ui/info-button';

/**
 * A chart card that previews compactly and expands into a modal on click. The
 * card advertises its interactivity (cursor, hover ring, an ⤢ hint); `children`
 * receives `expanded` so the caller can show a tall chart plus controls/legend in
 * the modal and a lean preview in the card.
 */
export function ExpandableChartShell({
  title,
  info,
  children,
}: {
  title: string;
  info?: ReactNode;
  children: (expanded: boolean) => ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const header = (
    <div className="flex items-center gap-1.5">
      <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
        {title}
      </h2>
      {info && <InfoButton label={title}>{info}</InfoButton>}
    </div>
  );

  return (
    <>
      <Card
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="group cursor-pointer p-5 transition-all hover:border-iris/50 hover:shadow-lg focus:outline-none focus-visible:border-iris"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          {header}
          <span
            aria-hidden
            className="text-xs text-muted opacity-60 transition-opacity group-hover:opacity-100"
            title="Click to expand"
          >
            ⤢
          </span>
        </div>
        {children(false)}
      </Card>

      {open && (
        <Modal open onClose={() => setOpen(false)} title={title} maxWidth="3xl">
          <div className="p-1">{children(true)}</div>
        </Modal>
      )}
    </>
  );
}
